<template>
  <div class="vehicles">
    <div
      v-if="loading"
      class="loader"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>
    <div v-else>
      <h1 v-if="textToSearch.length > 0">
        {{ $t('vehicles_overview.txt.search_results') }}
      </h1>
      <h1 v-else>
        {{ $t('vehicles_overview.txt.vehicles') }}
      </h1>
      <VehiclesSearch
        :products="products"
        :products-prismic="productsPrismic"
        :account-relations="accountRelations"
        @productsFilter="filtersReceived"
        @textToSearch="textToSearchReceived"
        @sortBy="sortByReceived"
        @viewType="viewTypeReceived"
      />

      <div
        v-if="noSearchResults"
        class="no-results"
      >
        <v-icon>mdi-truck</v-icon>
        <br>{{ getEmptyStateMessage() }}
      </div>
      <div
        v-else
        class="results"
      >
        <div class="results_info d-flex">
          <v-row>
            <v-col
              cols="12"
              lg="10"
            >
              <div v-if="textToSearch.length > 0">
                {{ $t('vehicles_overview.txt.found') }}
                <b> {{ filteredList.length }} </b>
                {{ $t('vehicles_overview.txt.results') }}:
              </div>
              <div v-else-if="page*itemsPerPage < filteredList.length">
                {{ $t('vehicles_overview.txt.showing') }}
                <b>{{ (page*itemsPerPage)-(itemsPerPage-1) }}-{{ page*itemsPerPage }} {{ $t('vehicles_overview.txt.of') }} {{ filteredList.length }}</b>
                {{ $t('vehicles_overview.txt.vehicles') }}:
              </div>
              <div v-else>
                {{ $t('vehicles_overview.txt.showing') }}
                <b> {{ (page*itemsPerPage)-(itemsPerPage-1) }}-{{ filteredList.length }} {{ $t('vehicles_overview.txt.of') }} {{ filteredList.length }} </b>
                {{ $t('vehicles_overview.txt.vehicles') }}:
              </div>
            </v-col>
            <v-col
              cols="0"
              lg="2"
              class="py-0 justify-lg-end"
            >
              <v-btn
                v-if="changeTrucks"
                class="d-none active-change-trucks elevation-0"
                :class="{'d-lg-flex': viewType == 'list'}"
                @click="activateChangeTrucks(false)"
              >
                {{ $t('vehicles_overview.txt.change_trucks') }}
              </v-btn>
              <v-btn
                v-else-if="hasOwnerRelation"
                class="d-none disable-change-trucks elevation-0"
                :class="{ 'd-lg-flex': viewType == 'list'}"
                @click="activateChangeTrucks(true)"
              >
                {{ $t('vehicles_overview.txt.change_trucks') }}
              </v-btn>
            </v-col>
          </v-row>
        </div>
        <div
          class="results_table"
          :class="{ 'wide-grid': viewType == 'list' && $vuetify.breakpoint.lgAndUp, 'manage-grid': changeTrucks}"
        >
          <v-row
            class="d-none"
            :class="{ 'd-lg-flex': viewType == 'list' }"
          >
            <v-col cols="12">
              <div
                v-if="changeTrucks"
                class="options"
              >
                <div class="start-spacer" />
                <span class="filter-label">{{ $t('service_details.txt.select') }}: </span>
                <a @click="selectByItem('all')"> {{ $t('service_details.txt.all') }} </a>
                <div class="vertical-spacer" />
                <a @click="selectByItem('thisPage')">{{ $t('service_details.txt.this_page') }} </a>
                <div class="horizontal-spacer" />
              </div>
            </v-col>
          </v-row>
          <v-row
            class="d-none header"
            :class="{ 'd-lg-flex': viewType == 'list' }"
          >
            <v-col
              v-if="changeTrucks"
              cols="1"
              class="pl-5 VhlCheck"
            >
              <v-checkbox
                v-model="checkSelected"
                class="check-item"
                :ripple="false"
                @change="setSelectedTrucks"
              />
            </v-col>
            <v-col
              cols="2"
              class="VhlName"
            >
              {{ $t('vehicles_overview.txt.vehicle_name') }}
            </v-col>
            <v-col
              cols="2"
              class="VhlLicPlate"
            >
              {{ $t('vehicles_overview.txt.license_plate') }}
            </v-col>
            <v-col
              v-if="!featureFlags.useVehicleApps || viewType != 'list'"
              cols="2"
              class="VhlType"
            >
              {{ $t('vehicles_overview.txt.type') }}
            </v-col>
            <v-col cols="2">
              {{ $t('vehicles_overview.txt.active_services') }}
            </v-col>
            <v-col
              v-if="hasAccountRelations"
              cols="1"
              class="VhlApps"
            >
              OWNER
            </v-col>
            <v-col
              v-if="featureFlags.useVehicleApps"
              cols="1"
              class="VhlApps"
            >
              {{ $t('vehicles_overview.txt.active_apps') }}
            </v-col>
            <v-col
              cols="2"
              class="VhlCost"
            >
              {{ $t('vehicles_overview.txt.costs') }}
            </v-col>
            <v-col
              cols="1"
              class="VhlAction"
            />
          </v-row>
          <v-row class="body">
            <v-col
              v-for="vehicle in visibleItemsPerPages"
              :key="vehicle.id"
              cols="12"
              sm="6"
              md="4"
              :items="visibleItemsPerPages"
              class="item py-3"
              :class="{ 'col-lg-12 py-lg-1': viewType == 'list' }"
            >
              <v-card
                class="pa-5"
                :disabled="changeTrucks && disabledVehicleInCart(vehicle.subscriptionNumber)"
                :class="{
                  'py-lg-1 px-lg-3': viewType == 'list',
                  selected: selected.includes(vehicle)
                }"
                @click="handleClick(vehicle.subscriptionNumber)"
              >
                <v-container
                  fluid
                  class="pa-0"
                >
                  <v-row>
                    <v-col
                      v-if="changeTrucks"
                      cols="1"
                      class="d-none align-center VhlCheck"
                      :class="{ 'pl-5 d-lg-flex': viewType == 'list'}"
                    >
                      <v-checkbox
                        v-model="selected"
                        :disabled="isThirdPartyVehicle(vehicle)"
                        class="check-item"
                        :value="vehicle"
                        :ripple="false"
                      />
                    </v-col>
                    <v-col
                      v-if="vehicle.vin__c || vehicle.vehicleName__c"
                      cols="12"
                      class="d-none align-center VhlName"
                      :class="{ 'col-lg-2 d-lg-flex order-lg-1': viewType == 'list' }"
                    >
                      <v-tooltip
                        content-class="vehiclevin-tooltip"
                        bottom
                        color="#4a5568"
                      >
                        <template v-slot:activator="{ on }">
                          <span
                            v-if="vehicle.vehicleName__c"
                            class="vehiclename"
                            v-on="on"
                          >{{ vehicle.vehicleName__c }}</span>
                          <span
                            v-else
                            class="vehiclename"
                            v-on="on"
                          >...{{ vehicle.vin__c.substring(vehicle.vin__c.length - 8) }}</span>
                        </template>
                        <div>
                          <div>
                            <span class="vehiclevin-span"> {{ $t('vehicles_overview.txt.vehicle_name') }}: </span>{{ vehicle.vehicleName__c }}
                          </div>
                          <div>
                            <span class="vehiclevin-span">{{ $t('vehicles_overview.txt.vin_number') }}: </span>{{ vehicle.vin__c }}
                          </div>
                        </div>
                      </v-tooltip>
                    </v-col>
                    <v-col
                      order="1"
                      cols="12"
                      class="VhlLicPlateText platenumber text-center justify-center platenumber align-center"
                      :class="{ 'col-lg-2 justify-lg-start order-lg-2': viewType == 'list', 'list-text': viewType == 'list' && $vuetify.breakpoint.lgAndUp }"
                    >
                      {{ vehicle.plateNumber__c }}
                    </v-col>
                    <v-col
                      order="2"
                      cols="12"
                      class="d-flex justify-center align-center"
                      :class="{ 'col-lg-2 d-lg-none': viewType == 'list' }"
                    >
                      <div class="truck-img-placeholder">
                        <v-img
                          class="truck-img"
                          :src="$t('trucks.img.'+ vehicle.dafType__c +'.img')"
                          :alt="$t('trucks.img.'+ vehicle.dafType__c +'.alt')"
                        />
                      </div>
                    </v-col>
                    <v-col
                      v-if="!featureFlags.useVehicleApps || viewType != 'list'"
                      order="3"
                      cols="12"
                      class="d-flex justify-center type align-center VhlType"
                      :class="{ 'col-lg-2 justify-lg-start': viewType == 'list', 'list-text': viewType == 'list' && $vuetify.breakpoint.lgAndUp }"
                    >
                      {{ vehicle.vehicleType__c }}
                    </v-col>
                    <v-col
                      order="5"
                      cols="12"
                      class="d-flex justify-center align-center"
                      :class="{ 'col-lg-2 justify-lg-start order-lg-4': viewType == 'list' }"
                    >
                      <div v-if="vehicle.activeProducts.length > 0">
                        <v-tooltip
                          v-for="(product, index) in vehicle.activeProducts"
                          :key="index"
                          content-class="service-tooltip"
                          top
                          color="#3c366b"
                        >
                          <template v-slot:activator="{ on }">
                            <v-img
                              class="service-icon"
                              :src="product.icon"
                              v-on="on"
                            />
                          </template>
                          <span class="tooltip-span">{{ product.name }}</span>
                        </v-tooltip>
                      </div>
                      <div v-else>
                        <span class="noservices">{{ $t('vehicles_overview.txt.no_services_selected') }}</span>
                      </div>
                    </v-col>
                    <v-col
                      v-if="hasAccountRelations"
                      order="4"
                      cols="12"
                      class="d-flex type align-center VhlApps"
                    >
                      {{ vehicle.ownerName }}
                    </v-col>
                    <v-col
                      v-if="featureFlags.useVehicleApps"
                      order="5"
                      cols="12"
                      class="d-flex VhlApps justify-center align-center"
                      :class="{'col-lg-2 justify-lg-start order-lg-4':viewType == 'list'}"
                      offset="0"
                    >
                      <div v-if="vehicle.activeAppsRender.length > 0">
                        <v-tooltip
                          v-for="(product, index) in vehicle.activeAppsRender"
                          :key="index"
                          content-class="service-tooltip"
                          top
                          color="#3c366b"
                        >
                          <template v-slot:activator="{ on }">
                            <v-img
                              v-if="product.icon"
                              class="service-icon"
                              :src="product.icon"
                              v-on="on"
                            />
                            <v-icon
                              v-else
                              class="activeApp-default-icon"
                              v-on="on"
                            >
                              mdi-application
                            </v-icon>
                          </template>
                          <span class="tooltip-span">{{ product.name }}</span>
                        </v-tooltip>
                        <div
                          v-if="vehicle.activeAppsExtraCount>0"
                          class="extraApps-counter d-flex"
                        >
                          <div class="counter-value">
                            +{{ vehicle.activeAppsExtraCount }}
                          </div>
                        </div>
                      </div>
                    </v-col>
                    <v-col
                      order="6"
                      cols="12"
                      class="text-center align-self-center VhlCost"
                      :class="{ 'col-lg-2 text-lg-left order-lg-5': viewType == 'list', 'grid-totals': viewType == 'grid' || $vuetify.breakpoint.mdAndDown }"
                    >
                      <div v-if="vehicle.totalMonth>0">
                        <b>{{ vehicle.totalMonth | formatPrice(currency.symbol) }} </b>{{ $t('vehicles_overview.txt.month') }}
                      </div>
                      <div v-if="vehicle.totalYear>0">
                        <b>{{ vehicle.totalYear | formatPrice(currency.symbol) }} </b>{{ $t('vehicles_overview.txt.year') }}
                      </div>
                    </v-col>
                    <v-col
                      v-if="changeTrucks"
                      order="7"
                      cols="12"
                      class="d-none align-center VhlAction"
                      :class="{ 'col-lg-1 d-lg-flex justify-lg-center': viewType == 'list' }"
                    >
                      <v-btn
                        class="button elevation-0"
                        :to="{ name: 'vehicle_details', params: { id: vehicle.subscriptionNumber }}"
                      >
                        {{ $t('vehicles_overview.txt.configure') }}
                      </v-btn>
                    </v-col>
                    <v-col
                      v-else
                      order="6"
                      cols="12"
                      class="d-none align-center VhlAction"
                      :class="{ 'col-lg-1 d-lg-flex justify-lg-center': viewType == 'list' }"
                    >
                      <v-btn
                        class="button elevation-0"
                        :to="{ name: 'vehicle_details', params: { id: vehicle.subscriptionNumber }}"
                      >
                        {{ $t('vehicles_overview.txt.configure') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <div class="results_pagination">
          <v-pagination
            v-model="page"
            color="pale"
            prev-icon="mdi-arrow-left"
            next-icon="mdi-arrow-right"
            :length="Math.ceil(filteredList.length/itemsPerPage)"
          />
        </div>
        <v-footer
          v-if="changeTrucks"
          fixed
          color="white"
          class="elevation-2 d-none"
          :class="{ 'd-lg-flex': viewType == 'list' }"
        >
          <v-container class="py-0">
            <v-row class="results_footer">
              <v-col
                class="align-self-center"
                cols="4"
                offset="2"
              >
                <v-icon
                  width="22"
                  color="steel"
                >
                  mdi-truck
                </v-icon>
                <span>
                  x <b>{{ selectedTrucksLength }} </b>
                </span>
              </v-col>
              <v-col
                class="justify-end align-self-center d-flex"
                cols="6"
              >
                <v-btn
                  class="results_footer_btn"
                  :class="selected.length == 0 ? 'disabled' : 'change-owner'"
                  :disabled="selected.length == 0"
                  @click="bulkChangeOwner()"
                >
                  {{ $t('vehicles_overview.txt.change_owner') }}
                </v-btn>
                <v-btn
                  class="results_footer_btn ml-5"
                  :class="selected.length == 0 ? 'disabled' : 'remove-vehicle'"
                  :disabled="selected.length == 0"
                  @click="bulkRemoveVehicle()"
                >
                  {{ $t('vehicles_overview.txt.remove_vehicle') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-container>
        </v-footer>
      </div>
    </div>
    <v-snackbar
      v-model="notification"
      top
      center
      :color="notificationType"
    >
      {{ notificationText }}
    </v-snackbar>
  </div>
</template>

<script>
import apiCalls from '@/helpers/apiCall'
import api from '@/helpers/api'
import { mapState, mapMutations, mapActions } from 'vuex'
import VehiclesSearch from '@/components/VehiclesSearch.vue'
import moment from 'moment'
import prismicService from '@/helpers/prismicService'
import visibilityHelper from '@/helpers/visibilityHelper'

export default {
  name: 'VehiclesOverview',
  components: {
    VehiclesSearch
  },
  data() {
    return {
      page: 1,
      pageCount: 0,
      itemsPerPage: 8,
      products: [],
      productsPrismic: [],
      textToSearch: '',
      sortBy: '',
      filters: '',
      flagList: false,
      viewType: 'list',
      selected: [],
      selectedBy: '',
      checkSelected: false,
      changeTrucks: false,
      notification: false,
      notificationText: '',
      notificationType: 'success',
      loading: false
    }
  },
  computed: {
    ...mapState('account', ['currency', 'featureFlags', 'accountRelations']),
    ...mapState('trucks', ['trucks']),
    ...mapState('checkout', ['checkout']),

    hasAccountRelations() {
      return visibilityHelper.hasAccountRelations(this.accountRelations)
    },

    hasOwnerRelation() {
      return visibilityHelper.hasOwnerRelation(this.accountRelations)
    },

    noSearchResults() {
      return (
        (this.trucks && this.trucks.length == 0) ||
        (this.textToSearch.length > 0 && this.filteredList.length == 0) ||
        (this.filters.length > 0 && this.filteredList.length == 0)
      )
    },
    filteredList() {
      let filteredVehicles = this.trucks
      if (this.filters && this.filters.length > 0) {
        this.filters.forEach(x => {
          filteredVehicles = filteredVehicles.filter(v => v.actServ.includes(x))
        })
      }
      if (this.textToSearch && this.flagList == false) {
        filteredVehicles = filteredVehicles.filter(vehicle => {
          return (
            (vehicle.vehicleName__c &&
              vehicle.vehicleName__c
                .toLowerCase()
                .includes(this.textToSearch.toLowerCase())) ||
            (vehicle.plateNumber__c &&
              vehicle.plateNumber__c
                .toLowerCase()
                .includes(this.textToSearch.toLowerCase())) ||
            (vehicle.vin__c &&
              vehicle.vin__c
                .toLowerCase()
                .includes(this.textToSearch.toLowerCase())) ||
            (vehicle.vehicleType__c &&
              vehicle.vehicleType__c
                .toLowerCase()
                .includes(this.textToSearch.toLowerCase())) ||
            (vehicle.ownerName &&
              vehicle.ownerName
                .toLowerCase()
                .includes(this.textToSearch.toLowerCase()))
          )
        })
      }
      if (this.sortBy.key === 'vinNumber') {
        filteredVehicles.sort((x, y) => {
          return x.vin__c < y.vin__c ? -1 : x.vin__c > y.vin__c ? 1 : 0
        })
      } else if (this.sortBy.key === 'date') {
        filteredVehicles.sort((x, y) => {
          if (x.contractEffectiveDate < y.contractEffectiveDate) return -1
          if (x.contractEffectiveDate > y.contractEffectiveDate) return 1
          return 0
        })
      } else if (this.sortBy.key === 'licensePlate') {
        filteredVehicles.sort((x, y) => {
          if (x.plateNumber__c < y.plateNumber__c) return -1
          if (x.plateNumber__c > y.plateNumber__c) return 1
          return 0
        })
      } else if (this.sortBy.key === 'ownerName') {
        filteredVehicles.sort((x, y) => {
          return x.ownerName > y.ownerName
            ? -1
            : x.ownerName < y.ownerName
            ? 1
            : 0
        })
      }
      return filteredVehicles
    },
    visibleItemsPerPages() {
      const visibleVehiclesPerPage = this.filteredList.slice(
        (this.page - 1) * this.itemsPerPage,
        this.page * this.itemsPerPage
      )

      this.getActiveIcons(visibleVehiclesPerPage)
      this.getActiveAppsIcons(visibleVehiclesPerPage)

      return visibleVehiclesPerPage
    },
    selectedTrucksLength() {
      if (this.selected) return this.selected.length
      return 0
    },
    vehicleInCart() {
      const array = []
      const x = this.checkout
        ? this.checkout.filter(
            x => x.productID == 'scrapped' || x.productID == 'changeOwner'
          )
        : false
      if (x && x.length > 0) {
        x.forEach(product => {
          product.rateplans.forEach(rp => {
            array.push(...rp.subscriptions)
          })
        })
      }
      return array
    }
  },
  watch: {
    changeTrucks() {
      this.visibleItemsPerPages
    }
  },
  async created() {
    this.loading = true
    try {
      await this.getProductsPrismic()
      await this.getVehicles()
      await this.getProducts()
      this.setVehiclesList()
    } catch (error) {
      console.error('Error loading vehicles data:', error)
    } finally {
      this.loading = false
    }
  },
  methods: {
    ...mapMutations('checkout', ['addTotalCheckout', 'setCheckout']),
    ...mapActions('trucks', ['getVehicles']),
    ...mapActions('trucks', ['invalidateTrucksCache']),
    ...mapActions('notification', ['setSnackbar']),
    ...mapActions('products', ['getServicesProducts']),

    setVehiclesList() {
      this.trucks.forEach(fv => {
        fv['actServ'] = ''
        fv.ratePlans.forEach(i => {
          if (i.productName) {
            fv['actServ'] += i.productSku + ' '
          }
        })
      })
    },

    async getProducts() {
      this.products = await this.getServicesProducts()
    },

    getProductsPrismic() {
      return prismicService
        .getProductCatalog()
        .then(catalog => (this.productsPrismic = catalog))
    },

    getIconUrl(name, active) {
      let url = ''
      this.productsPrismic.forEach(catalogProduct => {
        if (catalogProduct.externalKey === name) {
          if (active == true) url = catalogProduct.iconActiveUrl
          else url = catalogProduct.iconDisabledUrl
        }
      })
      return url
    },

    getPrismicInfo(productSku) {
      const { name: defaultName } =
        this.products.find(prod => prod.sku == productSku) || {}

      const productDisplay = { icon: '', name: defaultName || '' }

      const prismicInfo = this.productsPrismic.find(catalogProduct =>
        catalogProduct.externalKey.includes(productSku)
      )

      if (prismicInfo) {
        productDisplay.icon = prismicInfo.iconActiveUrl
        productDisplay.name = prismicInfo.title
      }

      return productDisplay
    },

    getActiveAppsIcons(visibleItems) {
      visibleItems.forEach(truck => {
        if (truck.activeAppsList && truck.activeAppsList.length) {
          truck.activeAppsRender = truck.activeAppsList.map(vehicleAppSku => {
            const monthlyCost = truck.ratePlans
              .filter(ratePlan => ratePlan.productSku === vehicleAppSku)
              .reduce((monthTotal, ratePlan) => {
                return (
                  monthTotal +
                  (ratePlan.ratePlanCharges || []).reduce(
                    (chargeTotal, charge) => chargeTotal + charge.price,
                    0
                  )
                )
              }, 0)

            truck.totalMonth += monthlyCost
            const prismicInfo = this.getPrismicInfo(vehicleAppSku)
            return prismicInfo || { name: '' }
          })

          const maxToDisplay = this.changeTrucks ? 3 : 4

          truck.activeAppsExtraCount = Math.max(
            truck.activeAppsRender.length - maxToDisplay,
            0
          )
          truck.activeAppsRender = truck.activeAppsRender.slice(0, maxToDisplay)
        } else {
          truck.activeAppsRender = []
        }
      })
    },

    getActiveIcons(visibleItems) {
      const subscribableProducts = []
      const internalProducts = []
      const todayDate = moment().format('YYYY-MM-DD')

      this.products.forEach(prod => {
        const isSubscribable = (prod.productRatePlans || []).find(
          rp => rp.isAllowedToSubscribe__c != 'False'
        )
        if (isSubscribable) subscribableProducts.push(prod)
        else internalProducts.push(prod)
      })
      visibleItems.forEach(truck => {
        let truckProducts = []
        let totalMonth = 0
        let totalYear = 0
        subscribableProducts.forEach(prod => {
          if (!visibilityHelper.isProductForVehicle(prod, truck)) {
            return
          }

          truckProducts.push({
            name: prod.name,
            sku: prod.sku,
            isLifetime: prod.isLifetime,
            isDafConnect: prod.isDafConnect,
            icon: this.getIconUrl(prod.sku, false),
            price: 0,
            period: ''
          })
        })
        internalProducts.forEach(prod => {
          //internal products can be shown if a rateplan is bought for it
          if (!truck.ratePlans.find(rp => rp.productSku == prod.sku)) return

          truckProducts.push({
            name: prod.name,
            sku: prod.sku,
            isLifetime: prod.isLifetime,
            isDafConnect: prod.isDafConnect,
            icon: this.getIconUrl(prod.sku, false),
            price: 0,
            period: ''
          })
        })

        truckProducts.forEach(ar => {
          truck.ratePlans.forEach(rp => {
            if (rp.productSku == ar.sku) {
              ar.icon = this.getIconUrl(rp.productSku, true)
              ar.activated = true
              rp.ratePlanCharges.forEach(rpc => {
                ar.price = rpc.effectiveStartDate <= todayDate ? rpc.price : 0
                ar.period = rpc.billingPeriod
              })
            }
          })
          this.productsPrismic.forEach(catalogProduct => {
            if (catalogProduct.externalKey.includes(ar.sku))
              ar.name = catalogProduct.title
          })
          if (ar.period === 'Month') totalMonth = totalMonth + ar.price
          if (ar.period === 'Annual') totalYear = totalYear + ar.price
        })

        // DAF connect and lifetime cannot be shown at the same time
        if (
          truckProducts.find(p => p.isDafConnect && p.activated) ||
          !this.featureFlags.useEssentials
        ) {
          truckProducts = truckProducts.filter(p => !p.isLifetime)
        } else {
          truckProducts = truckProducts.filter(p => !p.isDafConnect)
        }

        truckProducts.sort((a, b) => {
          return a.name > b.name ? 1 : -1
        })
        truck.totalMonth = totalMonth
        truck.totalYear = totalYear
        truck.activeProducts = truckProducts
      })
      return visibleItems
    },

    filtersReceived(servicesFiltered) {
      this.filters = servicesFiltered
    },
    textToSearchReceived(search) {
      return (this.textToSearch = search)
    },
    sortByReceived(sortBy) {
      return (this.sortBy = sortBy)
    },
    viewTypeReceived(viewType) {
      return (this.viewType = viewType)
    },
    selectByItem(selectByReceived) {
      this.selectedBy = selectByReceived
      if (this.selectedBy === 'all') {
        this.checkSelected = true
        this.selected = this.filteredList.filter(
          x =>
            this.disabledVehicleInCart(x.subscriptionNumber) === false &&
            this.isThirdPartyVehicle(x) === false
        )
      } else if (this.selectedBy === 'thisPage') {
        this.checkSelected = true
        this.selected = this.visibleItemsPerPages.filter(
          x =>
            this.disabledVehicleInCart(x.subscriptionNumber) === false &&
            this.isThirdPartyVehicle(x) === false
        )
      }
    },
    setSelectedTrucks() {
      if (this.checkSelected) {
        this.selected = this.visibleItemsPerPages.filter(
          x =>
            this.disabledVehicleInCart(x.subscriptionNumber) === false &&
            this.isThirdPartyVehicle(x) === false
        )
      } else this.selected = []
    },
    activateChangeTrucks(event) {
      this.changeTrucks = event
      this.selected = []
    },
    handleClick(subscriptionNumber) {
      if (!this.changeTrucks) {
        this.$router.push({
          name: 'vehicle_details',
          params: { id: subscriptionNumber }
        })
      }
    },
    disabledVehicleInCart(subscriptionNumber) {
      return this.vehicleInCart.some(
        x => x.subscriptionID == subscriptionNumber
      )
    },
    bulkChangeOwner() {
      this.selected.forEach(truck => {
        apiCalls
          .put(api.CartAdd, '', '', {
            subscriptionID: truck.subscriptionNumber,
            productRateplanID: '',
            rateplanID: '',
            productRateplanChargeID: '',
            productID: 'changeOwner',
            addSubscription: 0,
            metaData: {
              newOwner: '',
              cancelationDate: moment().format('YYYY-MM-DD')
            }
          })
          .then(res => {
            if (res.status == 200) {
              this.setCheckout(res.data.products)
              this.invalidateTrucksCache()
              this.addTotalCheckout(this.selected.length)
              this.setSnackbar({
                showing: true,
                type: 'success',
                text: this.$t('vehicles_overview.txt.notification_success')
              })
              this.selected = []
              this.changeTrucks = false
            } else {
              this.setSnackbar({
                showing: true,
                type: 'error',
                text: this.$t('vehicles_overview.txt.notification_error')
              })
            }
          })
      })
    },
    bulkRemoveVehicle() {
      this.selected.forEach(truck => {
        apiCalls
          .put(api.CartAdd, '', '', {
            subscriptionID: truck.subscriptionNumber,
            productRateplanID: '',
            rateplanID: '',
            productRateplanChargeID: '',
            productID: 'scrapped',
            addSubscription: 0,
            metaData: { cancelationDate: moment().format('YYYY-MM-DD') }
          })
          .then(res => {
            if (res.status == 200) {
              this.setCheckout(res.data.products)
              this.invalidateTrucksCache()
              this.addTotalCheckout(this.selected.length)
              this.setSnackbar({
                showing: true,
                type: 'success',
                text: this.$t('vehicles_overview.txt.notification_success')
              })
              this.selected = []
              this.changeTrucks = false
            } else {
              this.setSnackbar({
                showing: true,
                type: 'error',
                text: this.$t('vehicles_overview.txt.notification_error')
              })
            }
          })
      })
    },
    isThirdPartyVehicle(vehicle) {
      return visibilityHelper.isThirdPartyVehicle(vehicle)
    },

    getEmptyStateMessage() {
      // If there are no vehicles in the total (empty array)
      if (this.trucks && this.trucks.length === 0) {
        // Check if the translation key exists, otherwise use a default message
        const noVehiclesKey = 'vehicles_overview.txt.no_vehicles_available'
        if (this.$te(noVehiclesKey)) {
          return this.$t(noVehiclesKey)
        }
        return 'No vehicles available'
      }
      // If there is an active search but no results
      if (this.textToSearch.length > 0) {
        return this.$t('vehicles_overview.txt.empty_search')
      }
      // If there are active filters but no results
      if (this.filters && this.filters.length > 0) {
        return this.$t('vehicles_overview.txt.empty_search')
      }
      // Fallback for empty search
      return this.$t('vehicles_overview.txt.empty_search')
    }
  }
}
</script>
<style lang="scss">
.vehicles {
  .loader {
    text-align: center;
    padding-top: 48px;
  }
  h1 {
    margin-bottom: 22px;
    font-size: 3rem;
    font-weight: 300;
    line-height: normal;
    color: var(--v-dark-base);
    text-align: left;
  }
  .no-results {
    font-size: 1.375rem;
    line-height: normal;
    text-align: center;
    color: var(--v-bluegrey-base);
    .v-icon {
      font-size: 100px;
      color: var(--v-cloudy-base);
    }
  }
  .results {
    margin-top: 32px;
    margin-bottom: 72px;
    &_info {
      font-size: 1rem;
      line-height: normal;
      color: var(--v-slate-base);
      .active-change-trucks {
        width: 100%;
        height: 32px;
        border-radius: 3px;
        background-color: var(--v-peacock-base) !important;
        color: white;
        font-size: 0.7rem;
      }
      .disable-change-trucks {
        width: 100%;
        height: 32px;
        border-radius: 3px;
        border: solid 1px var(--v-cloudy-base);
        background-color: white !important;
        color: var(--v-steel-base);
        font-size: 0.7rem;
      }
    }
    &_table {
      margin-bottom: 25px;
      .header {
        padding: 0 12px;
        .col {
          text-transform: uppercase;
          font-size: 0.8125rem;
          font-weight: 500;
          line-height: normal;
          color: var(--v-bluegrey-base);
          padding: 10px;
          border: 1px solid;
          text-overflow: ellipsis;
          overflow: hidden;
          white-space: nowrap;
        }
        .check-item {
          margin: 0;
          padding: 0;
          .v-input--selection-controls__input {
            width: 16px;
            height: 16px;
            border-radius: 4px;
            background-color: var(--v-pale-three-base);

            i {
              color: var(--v-pale-three-base);
            }
          }
        }
      }
      .body {
        .item {
          .v-card {
            border-radius: 6px;
            box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.18);
            color: var(--v-slate-base);
            &:hover {
              background-color: var(--v-pale-two-base);
            }
            &.selected {
              background-color: var(--v-peacock-base);
              color: white;
              .v-input--selection-controls__input {
                background-color: var(--v-peacock-base) !important;
                i {
                  color: var(--v-pale-three-base) !important;
                }
              }
              .vehiclename {
                color: white !important;
              }
              .platenumber {
                color: white !important;
              }
              .type {
                color: white !important;
              }
            }
            .vehiclename {
              font-weight: bold;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              font-size: 1rem;
              font-weight: normal;
              line-height: normal;
              letter-spacing: normal;
              color: var(--v-peacock-base);
              text-decoration: underline;
            }
            .platenumber {
              line-height: normal;
              color: var(--v-slate-base);
              font-size: 1.125rem;
              font-weight: bold;
              &.list-text {
                font-size: 1rem;
                font-weight: normal;
              }
            }
            .type {
              line-height: normal;
              color: var(--v-slate-base);
              font-size: 0.75rem;
              font-weight: bold;
              &.list-text {
                font-size: 1rem;
                font-weight: normal;
              }
            }
            .truck-img-placeholder {
              height: 100px;

              .truck-img {
                max-width: 80px;
              }
            }
            .service-icon {
              width: 30px;
              height: 30px;
              float: left;
            }
            .noservices {
              font-size: 0.6875rem;
              font-weight: bold;
              color: var(--v-bluegrey-base);
              text-transform: uppercase;
            }
            .button {
              font-size: 0.75rem;
              font-weight: bold;
              line-height: normal;
              text-align: center;
              color: var(--v-mid-base);
              max-width: 110px;
              height: 32px;
              border-radius: 3px;
              background-color: var(--v-ice-base);
            }
            .check-item {
              .v-input--selection-controls__input {
                width: 16px;
                height: 16px;
                border-radius: 4px;
                background-color: var(--v-pale-three-base);

                i {
                  color: var(--v-pale-three-base);
                }
              }
            }
            .grid-totals {
              height: 72px;
            }
            .activeApp-default-icon {
              color: var(--v-info-darken1);
            }
            .extraApps-counter {
              width: 30px;
              height: 30px;
              padding-left: 10px;
              align-items: center;
              justify-content: center;
              .counter-value {
                border: 1px solid;
                border-radius: 10px;
                color: var(--v-peacock-base) !important;
                border-color: var(--v-bluegrey-base) !important;
                padding: 0px 5px;
                font-size: 14px;
                height: 30px;
                line-height: 28px;
              }
            }
          }
        }
      }
      .options {
        display: flex;
        align-items: center;
        padding-left: 16px;

        a {
          margin: 0 10px;
          font-size: 0.875rem;
          text-decoration: underline;
        }

        .start-spacer {
          width: 20px;
          height: 20px;
          margin-top: 20px;
          margin-left: 12px;
          border-top: 1px solid var(--v-cloudy-base);
          border-left: 1px solid var(--v-cloudy-base);
        }

        .vertical-spacer {
          border-left: 1px solid var(--v-cloudy-base);
          color: var(--v-cloudy-base);
          height: 16px;
          margin-top: 2px;
        }

        .horizontal-spacer {
          border-top: 1px solid var(--v-cloudy-base);
          flex-grow: 1;
        }

        .filter-label {
          font-size: 0.875rem;
          font-weight: bold;
          text-align: right;
          color: var(--v-slate-base);
          margin: 0 10px;
          &.mobile {
            text-align: left;
            width: 100px;
          }
        }

        .filter-items {
          max-width: 170px;
          height: 32px;
          font-size: 0.875rem;
          &.mobile {
            width: 100%;
            max-width: none;
          }

          .v-input {
            &__slot {
              min-height: 32px !important;
            }

            &__append-inner {
              margin-top: 0 !important;
            }
          }
        }
      }
      &.wide-grid {
        .col {
          border: 0px solid;
          &.VhlName {
            flex: 0 0 15%;
            max-width: 15%;
          }
          &.VhlLicPlate {
            flex: 0 0 14%;
            max-width: 14%;
          }
          &.VhlLicPlateText {
            flex: 0 0 14%;
            max-width: 14%;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          &.VhlType {
            flex: 0 0 12%;
            max-width: 12%;
          }
          &.VhlCost {
            flex: 0 0 13%;
            max-width: 13%;
          }
          &.VhlApps {
            flex: 0 0 15%;
            max-width: 15%;
          }
          &.VhlAction {
            flex: 0 0 10%;
            max-width: 10%;
            padding-left: 0;
            padding-right: 0;
          }
          &.VhlCheck {
            flex: 0 0 5%;
            max-width: 5%;
          }
        }
        .header {
          .col {
            &.VhlApps {
              flex: 0 0 15%;
              max-width: 15%;
            }
          }
        }
        &.manage-grid {
          .VhlName {
            flex: 0 0 14%;
            max-width: 14%;
          }
          .VhlApps {
            flex: 0 0 13%;
            max-width: 13%;
          }
          .header {
            .VhlApps {
              flex: 0 0 13%;
              max-width: 13%;
              padding: 12px;
            }
          }
        }
      }
    }
    &_pagination {
      .v-pagination {
        &__item {
          font-weight: bold;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
          margin: 0 8px;
          &:focus {
            outline: none !important;
          }
          &--active {
            background-color: var(--v-secondary-base) !important;
            color: var(--v-darkgrey-blue-base) !important;
            box-shadow: none;
          }
        }
        &__navigation {
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
          margin: 0 40px;
        }
        .v-icon {
          font-size: 1.25rem;
          color: var(--v-slate-base);
        }
      }
    }
    &_footer {
      &_btn {
        width: 192px;
        height: 48px !important;
        border-radius: 5px;
        font-weight: bold;
        text-align: center;
        &.change-owner {
          background-image: linear-gradient(to bottom, #266cab 0%, #01529c);
          color: white;
        }
        &.remove-vehicle {
          background-image: linear-gradient(
            to bottom,
            #c53030 0%,
            #9b2c2c 100%
          );
          color: white;
        }
        &.disabled {
          background-color: var(--v-cloudy-base) !important;
          color: white !important;
        }
      }
      &_costs {
        font-size: 1.25rem;
        color: var(--v-slate-base);
      }
      &_chooseperiod {
        .label {
          font-size: 0.75rem;
          font-weight: bold;
          color: var(--v-peacock-base);
          text-transform: uppercase;
        }
        .buttons {
          .v-btn {
            width: 128px;
            height: 32px;
            border-radius: 3px;
            border: solid 1px var(--v-cloudy-base);
            background-color: white !important;
            font-size: 0.75rem;
            text-align: center;
            color: var(--v-steel-base);

            &.v-btn--active {
              background-color: var(--v-peacock-base) !important;
              color: white !important;
            }

            &.disabled {
              color: white !important;
            }
          }
        }
      }
    }
  }
}
.service-tooltip {
  height: 34px !important;
  min-height: 34px !important;
  background: rgb(60, 54, 107) !important;
  .tooltip-span {
    font-size: 0.75rem;
    font-weight: bold;
    line-height: normal;
    text-align: center;
    color: white;
  }
  &:before {
    content: '';
    display: block;
    width: 0 !important;
    height: 0 !important;
    border-top: 0 !important;
    border-bottom: 12px solid transparent !important;
    border-left: 12px solid var(--v-darkgrey-blue-two-base) !important;
    -webkit-transform: rotate(225deg) !important;
    position: absolute !important;
    top: 27px !important;
    left: 45% !important;
  }
}
.vehiclevin-tooltip {
  min-height: 32px !important;
  border-radius: 16px !important;
  background: rgb(74, 85, 104) !important;
  .vehiclevin-span {
    text-transform: uppercase;
    font-size: 0.75rem;
    font-weight: bold;
    line-height: normal;
    text-align: center;
    color: white;
  }
}
</style>